// Conquer the Flag on the site: the engine wrapped for the room relay.
// The relay sends the whole state on every action, so the wrapper keeps it small:
// the game log is trimmed to its most recent entries, and the history is kept as
// compact replay tokens (the same format the CTF Replay Reviewer reads).

import {
  DEFAULT_CONFIG, UNIT_PRESETS, RULES_VERSION, DEFAULT_TEAM,
  createGame, applyAction, actorTeam, playerOf, getBotAction,
} from './capture-the-flag';
import { encodeAction, decodeToken, parseReplay, REPLAY_FORMAT } from './replay';
import { parsePosition, POSITION_FORMAT } from './playground/edit';
import { decodeTeam, Team as TeamCode } from './teamcode';
import type { AbilityId, CtfAction, CtfConfig, CtfPlayer, CtfState, EffectId, OutpostLayout, Team } from './types';

// Settings the waiting room offers, in two groups; everything else is DEFAULT_CONFIG.
export interface CtfRoomConfig {
  // Win condition (the first flag home wins; these can end it sooner)
  mercyRounds?: number;       // 0 = off
  dominationCells?: number;   // 0 = off; DOMINATION_DEFAULT when switched on
  // Map conditions
  units?: string;             // a key of UNIT_PRESETS: both teams' squad unless a team code says otherwise
  teamA?: string;             // Blue's team code from the team editor ('' or invalid: the unit set)
  teamB?: string;             // Red's
  outposts?: OutpostLayout;
  centre?: string;            // '' (random: Swiftness or Watchtower) or the EffectId the centre outpost always gets (default Magnet)
  flagWeight?: number;
  prisons?: string;           // '' off, 'on' (older rooms may say 'sentence': also on)
  // Each team's army ability ('' or an AbilityId; see CtfConfig)
  abilityA?: string;
  abilityB?: string;
  demo?: boolean;             // the host plays both sides; everyone else watches        // the flag carrier has this much less Move (at least 1); 0 = off
}

export const CTF_ROOM_DEFAULT_CONFIG: CtfRoomConfig = {
  mercyRounds: 0, dominationCells: 0, units: DEFAULT_TEAM, teamA: '', teamB: '', outposts: 'centre', centre: 'magnet',
  flagWeight: DEFAULT_CONFIG.carrierMovePenalty,
  abilityA: '', abilityB: '', prisons: 'on',   // prisons on by default (2026-10-15)
};

export const MERCY_OPTIONS = [10, 15, 20, 25, 30];
// Domination: cells to hold at the end of a round to win (19 cells on the map).
export const DOMINATION_DEFAULT = 14;
export const DOMINATION_MAX = 19;
export const FLAG_WEIGHT_MAX = 6;

export interface CtfRoomState {
  game: CtfState;
  seed: number;
  tokens: string[];           // every action so far, as replay tokens
  fromPosition?: boolean;     // loaded from a set-up position: there is no replay, only positions
  // Free take-backs: the player to act can go back to this many actions (the start of their activation,
  // or just after its last dice roll, whichever is later). Absent between activations.
  freeFrom?: number;
  rollback?: RollbackRequest | null;   // a rollback someone asked for, waiting for the other player
  rewound?: string | null;             // what the last agreed rollback went back to, until the next action
}

// A rollback: back to the game as it was after `at` actions. Asked for by one player; the other agrees.
export interface RollbackRequest { by: string; at: number; label: string }
export interface RollbackPoint { at: number; label: string; team: Team }

const LOG_KEEP = 150;

// A team code's squad, or null when it is blank or can't be read.
export function teamFromCode(code: string | undefined): TeamCode | null {
  if (!code?.trim()) return null;
  try { return decodeTeam(code); } catch { return null; }
}

const ABILITY_IDS: AbilityId[] = ['reroll', 'pass', 'sprint', 'rally'];
const asAbility = (x: string | undefined): AbilityId | null => (ABILITY_IDS.includes(x as AbilityId) ? (x as AbilityId) : null);

export function toEngineConfig(rc: CtfRoomConfig): CtfConfig {
  const preset = (UNIT_PRESETS[rc.units ?? DEFAULT_TEAM] ?? UNIT_PRESETS[DEFAULT_TEAM]).squads;
  return {
    ...DEFAULT_CONFIG,
    squads: { A: teamFromCode(rc.teamA)?.squad ?? preset.A, B: teamFromCode(rc.teamB)?.squad ?? preset.B },
    mercyRounds: rc.mercyRounds ?? DEFAULT_CONFIG.mercyRounds,
    dominationCells: Math.max(0, Math.min(DOMINATION_MAX, Math.round(rc.dominationCells ?? 0))),
    outposts: rc.outposts ?? DEFAULT_CONFIG.outposts,
    centreEffect: (rc.centre || null) as EffectId | null,
    carrierMovePenalty: Math.max(0, Math.min(FLAG_WEIGHT_MAX, Math.round(rc.flagWeight ?? DEFAULT_CONFIG.carrierMovePenalty))),
    abilities: { A: asAbility(rc.abilityA), B: asAbility(rc.abilityB) },
    prisons: rc.prisons === 'on' || rc.prisons === 'sentence',
  };
}

export function createCtfRoomGame(rc: CtfRoomConfig, players: { id: string; name: string; isBot?: boolean }[], seed: number): CtfRoomState {
  return { game: createGame(toEngineConfig(rc), players, seed), seed: seed | 0, tokens: [] };
}

// A fresh game with the room's settings and the same players on the same sides (rematch, restart).
export function restartCtfRoomGame(rc: CtfRoomConfig, game: CtfState, seed: number): CtfRoomState {
  return createCtfRoomGame(rc, game.players.map(p => ({ id: p.id, name: p.name, isBot: p.isBot })), seed);
}

// A replay (played through to its end) or a playground position, loaded into a room: the room's
// players take the two sides. Throws an Error saying what is wrong with the text.
export function loadIntoRoom(text: string, players: CtfPlayer[]): CtfRoomState {
  const t = text.trim();
  const first = t.split(/\s+/)[0];
  if (first === REPLAY_FORMAT) {
    const { header, tokens } = parseReplay(t);
    let game = createGame(header.config, players.map(p => ({ id: p.id, name: p.name, isBot: p.isBot })), header.seed);
    const done: string[] = [];
    for (const token of tokens) {
      const next = applyAction(game, decodeToken(game, token));
      if (next === game) throw new Error(`The replay stops making sense at action ${done.length + 1} ("${token}"): it may be from other rules.`);
      game = next;
      done.push(token);
    }
    if (game.log.length > LOG_KEEP) game.log = game.log.slice(-LOG_KEEP);
    return { game, seed: header.seed | 0, tokens: done };
  }
  if (first === POSITION_FORMAT) {
    const { game, seed } = parsePosition(t);
    return { game: { ...game, players: players.map(p => ({ ...p })) }, seed: seed | 0, tokens: [], fromPosition: true };
  }
  throw new Error(`Paste a replay (starting "${REPLAY_FORMAT}") or a position (starting "${POSITION_FORMAT}").`);
}

// Returns the same object when the action is illegal, like the engine.
export function applyRoomAction(rs: CtfRoomState, action: CtfAction): CtfRoomState {
  const next = applyAction(rs.game, action);
  if (next === rs.game) return rs;
  const token = encodeAction(rs.game, action);
  if (next.log.length > LOG_KEEP) next.log = next.log.slice(-LOG_KEEP);
  const tokens = [...rs.tokens, token];
  return { ...rs, game: next, tokens, freeFrom: freeFromAfter(rs, next, tokens.length), rewound: null };
}

// Where free take-backs stop after an action: the activation's start, moved up past any dice roll
// (the game's random state changed: there is now something the player didn't know before).
function freeFromAfter(rs: CtfRoomState, next: CtfState, n: number): number | undefined {
  if (!next.active || next.phase !== 'playing') return undefined;
  const sameActivation = rs.game.active && rs.game.active.unitId === next.active.unitId && actorTeam(rs.game) === actorTeam(next);
  if (next.rng !== rs.game.rng) return n;
  return sameActivation && rs.freeFrom !== undefined ? rs.freeFrom : n - 1;
}

// The game rebuilt from its start with its first `n` actions (null for a game loaded from a position,
// which has no actions to rebuild from). Games are deterministic, so this is the game as it was then.
export function rebuild(rs: CtfRoomState, n: number): CtfRoomState | null {
  if (rs.fromPosition || n < 0 || n > rs.tokens.length) return null;
  const g0 = createGame(rs.game.config, rs.game.players.map(p => ({ id: p.id, name: p.name, isBot: p.isBot })), rs.seed);
  let out: CtfRoomState = { game: g0, seed: rs.seed, tokens: [] };
  for (const token of rs.tokens.slice(0, n)) {
    const next = applyRoomAction(out, decodeToken(out.game, token));
    if (next === out) return null;
    out = next;
  }
  return { ...out, rollback: null, rewound: null };
}

// Take back the last action, or everything back to the start of the activation (free take-backs:
// only the player to act, and never past a dice roll). null when there is nothing to take back.
export function takeBack(rs: CtfRoomState, all: boolean): CtfRoomState | null {
  if (rs.freeFrom === undefined || rs.tokens.length <= rs.freeFrom) return null;
  const back = rebuild(rs, all ? rs.freeFrom : rs.tokens.length - 1);
  return back && { ...back, rollback: rs.rollback ?? null };
}

// Where a rollback can go back to: the start of every activation so far, newest first.
export function rollbackPoints(rs: CtfRoomState): RollbackPoint[] {
  if (rs.fromPosition) return [];
  const names: Record<Team, string> = { A: playerOf(rs.game, 'A').name, B: playerOf(rs.game, 'B').name };
  const out: RollbackPoint[] = [];
  let g = createGame(rs.game.config, rs.game.players.map(p => ({ id: p.id, name: p.name, isBot: p.isBot })), rs.seed);
  const count: Record<string, number> = {};
  for (let i = 0; i <= rs.tokens.length; i++) {
    const team = actorTeam(g);
    if (team && !g.active && g.phase === 'playing') {
      const key = `${g.round}${team}`;
      count[key] = (count[key] ?? 0) + 1;
      if (i < rs.tokens.length) out.push({ at: i, team, label: `Round ${g.round}: ${names[team]}'s activation ${count[key]}` });
    }
    if (i === rs.tokens.length) break;
    try { g = applyAction(g, decodeToken(g, rs.tokens[i])); } catch { break; }
  }
  return out.reverse();
}

// Ask for a rollback, answer one, or call it off. Applying an agreed one rebuilds the game.
export function requestRollback(rs: CtfRoomState, by: string, point: RollbackPoint): CtfRoomState {
  return { ...rs, rollback: { by, at: point.at, label: point.label } };
}
export function answerRollback(rs: CtfRoomState, accept: boolean): CtfRoomState {
  const r = rs.rollback;
  if (!r) return rs;
  if (!accept) return { ...rs, rollback: null };
  const back = rebuild(rs, r.at);
  return back ? { ...back, rewound: r.label } : { ...rs, rollback: null };
}

// The bot's next action when the team to act is played by `botIds`, else null.
export function botMove(rs: CtfRoomState, botIds: Set<string>, bot: (p: CtfPlayer, s: CtfState) => CtfAction | null = getBotAction): CtfAction | null {
  const team = actorTeam(rs.game);
  if (!team) return null;
  const player = playerOf(rs.game, team);
  if (!botIds.has(player.id)) return null;
  return bot(player, rs.game);
}

// The game so far as replay text for the CTF Replay Reviewer.
export function replayText(rs: CtfRoomState): string {
  const names: Record<Team, string> = { A: playerOf(rs.game, 'A').name, B: playerOf(rs.game, 'B').name };
  const lines = [
    `${REPLAY_FORMAT} seed=${rs.seed} engine=site-${RULES_VERSION}`,
    `players ${JSON.stringify(names)}`,
    `config ${JSON.stringify(rs.game.config)}`,
  ];
  for (let i = 0; i < rs.tokens.length; i += 24) lines.push(rs.tokens.slice(i, i + 24).join(' '));
  return lines.join('\n') + '\n';
}
